import { useCallback, useRef, useState } from "react";
import { browserTimezone, localDateOnly } from "../../app/helpers.js";
import { copy } from "../../app/i18n.js";
import { loadChoreData } from "../../app/loaders.js";
import { toastLinkForEntity } from "../../app/entityRoutes.js";
import type {
  Attachment,
  ChoreAnalytics,
  ChoreCategory,
  ChoreOccurrence,
  ChoreTemplate,
  ChoreTimelineEntry,
  ChoreView,
  EntityReferenceId,
  HouseholdMember,
  Location,
  User
} from "../../app/types.js";
import { api, deleteJson, patchJson, postJson } from "../../lib/api.js";
import { attachmentUploadBody } from "../../lib/attachments.js";
import { backgroundUploadFailureMessage, backgroundUploads } from "../../lib/backgroundUploads.js";
import type { RefreshScope, RunOptions, ToastAction, ToastOptions } from "../context.js";
import { chorePayload, nextSnoozeDate } from "./formPayload.js";

/**
 * The chores feature's state, loader and mutations (#1682, the pilot for
 * "features own their state").
 *
 * These used to be eight `useState`s, `loadChores` and eleven handlers in
 * `App.tsx`, which meant a chores change read the whole shell. They are two
 * hooks here because the shell defines what they need in two places:
 * `useChoresState` needs only the signed-in user and runs early enough for the
 * shell's tab loader to call `loadChores`; `useChoreMutations` needs `run`,
 * the toast and the refresh helpers, which the shell defines later. The shell
 * calls each once and spreads the result into the provider value; every view
 * reads them through `useChores()`.
 */

// The chores list, as a toast link (#586). Used by every toast about an
// occurrence that just LEFT that list — completing, snoozing and skipping all
// drop it from the loaded set, so a link to the chore itself would land on
// /chores?taskId=… and find nothing to open. Bulk toasts use it for the plainer
// reason that they touched many rows.
//
// Module scope because it never varies: as a render-local it would be a fresh
// object every pass and defeat the memoized callbacks that carry it.
export const choresListLink = toastLinkForEntity("Open chores", "chore");

export type ChoresState = {
  choreView: ChoreView;
  setChoreView: (view: ChoreView) => void;
  choreOccurrences: ChoreOccurrence[];
  choreCategories: ChoreCategory[];
  choreTemplates: ChoreTemplate[];
  choreAnalytics: ChoreAnalytics | null;
  choreUsers: HouseholdMember[];
  choreTimeline: ChoreTimelineEntry[];
  setChoreTimeline: (timeline: ChoreTimelineEntry[]) => void;
  selectedChore: ChoreOccurrence | null;
  setSelectedChore: (chore: ChoreOccurrence | null) => void;
  loadChores: () => Promise<void>;
  /** Drop everything on a household switch or sign-out. */
  resetChores: () => void;
};

export function useChoresState({ user, setLocations }: {
  user: User | null;
  /** The chores payload carries the household's places; the shell owns that list. */
  setLocations: (locations: Location[]) => void;
}): ChoresState {
  const [choreView, setChoreView] = useState<ChoreView>("all");
  const [choreOccurrences, setChoreOccurrences] = useState<ChoreOccurrence[]>([]);
  const [choreCategories, setChoreCategories] = useState<ChoreCategory[]>([]);
  const [choreTemplates, setChoreTemplates] = useState<ChoreTemplate[]>([]);
  const [choreAnalytics, setChoreAnalytics] = useState<ChoreAnalytics | null>(null);
  const [choreUsers, setChoreUsers] = useState<HouseholdMember[]>([]);
  const [selectedChore, setSelectedChore] = useState<ChoreOccurrence | null>(null);
  const [choreTimeline, setChoreTimeline] = useState<ChoreTimelineEntry[]>([]);
  // Category defaults are seeded server-side at signup; the client backfill only
  // needs to run on the first chores load of a session, not on every refresh.
  const choresSeededRef = useRef(false);

  const loadChores = useCallback(async () => {
    if (!user) return;
    const next = await loadChoreData(choreView, { seedDefaults: !choresSeededRef.current });
    choresSeededRef.current = true;
    setLocations(next.locations.map((location) => ({ ...location, description: "" })));
    setChoreUsers(next.users);
    setChoreOccurrences(next.occurrences);
    // Keep the open details editor in sync with the refetched occurrence;
    // otherwise a saved edit keeps showing the stale pre-edit values.
    setSelectedChore((current) => {
      if (!current) return current;
      return next.occurrences.find((occurrence) => occurrence.id === current.id)
        ?? next.occurrences.find((occurrence) => occurrence.taskId === current.taskId)
        ?? current;
    });
    setChoreCategories(next.categories);
    setChoreTemplates(next.templates);
    setChoreAnalytics(next.analytics);
  }, [choreView, setLocations, user]);

  const resetChores = useCallback(() => {
    setChoreOccurrences([]);
    setChoreTemplates([]);
    setChoreAnalytics(null);
    setChoreUsers([]);
    setSelectedChore(null);
    setChoreTimeline([]);
    choresSeededRef.current = false;
  }, []);

  return {
    choreView,
    setChoreView,
    choreOccurrences,
    choreCategories,
    choreTemplates,
    choreAnalytics,
    choreUsers,
    choreTimeline,
    setChoreTimeline,
    selectedChore,
    setSelectedChore,
    loadChores,
    resetChores
  };
}

export type ChoreMutations = {
  createChore: (form: HTMLFormElement) => Promise<EntityReferenceId | null>;
  uploadChoreAttachments: (taskId: EntityReferenceId, files: File[]) => void;
  updateChore: (chore: ChoreOccurrence, form: HTMLFormElement) => Promise<void>;
  deleteChore: (chore: ChoreOccurrence) => Promise<void>;
  completeChoreOccurrence: (occurrence: ChoreOccurrence) => Promise<void>;
  snoozeChoreOccurrence: (occurrence: ChoreOccurrence) => Promise<void>;
  skipChoreOccurrence: (occurrence: ChoreOccurrence) => Promise<void>;
  reopenChoreOccurrence: (occurrence: ChoreOccurrence) => Promise<void>;
  applyChoreTemplate: (template: ChoreTemplate) => Promise<void>;
  openChoreTimeline: (chore: ChoreOccurrence) => Promise<void>;
};

export type ChoreMutationDeps = {
  run: (action: () => Promise<void>, options?: RunOptions) => Promise<boolean>;
  showToast: (message: string, options?: ToastAction[] | ToastOptions) => void;
  setError: (message: string) => void;
  refreshAfterOwnChange: (scope: RefreshScope) => Promise<void>;
  /** The shell's undo affordance for occurrences that just left the list. */
  occurrenceUndoToastActions: (
    occurrences: Array<{ id: EntityReferenceId }>,
    reason: string,
    refresh: RefreshScope,
    removeOccurrenceIds?: EntityReferenceId[]
  ) => ToastAction[];
};

export function useChoreMutations(
  state: Pick<ChoresState, "setChoreTimeline" | "setSelectedChore">,
  { run, showToast, setError, refreshAfterOwnChange, occurrenceUndoToastActions }: ChoreMutationDeps
): ChoreMutations {
  const { setChoreTimeline, setSelectedChore } = state;

  const createChore = useCallback(async (form: HTMLFormElement) => {
    let createdTaskId: EntityReferenceId | null = null;
    let created = false;
    // Block only on persisting the chore itself, not on the follow-up list
    // refresh — the caller can navigate the moment the chore is saved.
    await run(async () => {
      const payload = chorePayload(form);
      // An undated task is only valid inside a project (a planning to-do),
      // so it goes through the project endpoint; /api/v1/chores requires a date.
      const endpoint = payload.projectId && !payload.dueDate
        ? `/api/v1/projects/${payload.projectId}/tasks`
        : "/api/v1/chores";
      const chore = await postJson<{ id: EntityReferenceId }>(endpoint, payload);
      createdTaskId = chore.id;
      form.reset();
      created = true;
    }, { refresh: false });
    if (created && createdTaskId != null) {
      // Refresh the list in the background so the new chore appears promptly.
      // This is the refresh for *this* create, so the realtime echo of the same
      // create must not run a second full chore load behind it (#520).
      void refreshAfterOwnChange("chores");
      // The old "View" action button is now the first-row link (#586): both
      // navigated to the same place, and a link is the right affordance for it.
      showToast("Chore added", {
        link: toastLinkForEntity("Open chore", "task", createdTaskId),
        actions: [
          {
            label: "Undo",
            onClick: () => {
              // Stop any attachments still uploading for this chore (#522) —
              // otherwise they race the delete and report a failure for a chore
              // that no longer exists.
              backgroundUploads.cancel(`chore:${createdTaskId}`);
              void run(async () => {
                await deleteJson<{ ok?: true }>(`/api/v1/chores/${createdTaskId}`);
              }, { refresh: "chores", successMessage: "Chore removed" });
            }
          }
        ]
      });
    }
    return createdTaskId;
  }, [refreshAfterOwnChange, run, showToast]);

  // Upload chore attachments in the background so a create doesn't block on big
  // files. The chore is already saved before this runs, so it is never lost; a
  // failed upload surfaces a toast (not silent) and a beforeunload guard warns
  // if the tab is closed while uploads are still in flight.
  //
  // The mechanism lives in lib/backgroundUploads (#522) so other surfaces can
  // reuse it; the toast wording and refresh policy stay here.
  const uploadChoreAttachments = useCallback((taskId: EntityReferenceId, files: File[]) => {
    backgroundUploads.start<Attachment>({
      key: `chore:${taskId}`,
      files,
      // Through the shared body builder, not a hand-rolled FormData (codex
      // round 1). #1176 made the kind required for documents, and this caller
      // was the one that still appended only the file — so attaching a PDF while
      // creating a chore failed with a 400 nobody asked for. The chore surface
      // has no kind picker, so it declares the same `other` an undeclared upload
      // used to become; the builder is what guarantees it arrives BEFORE the
      // file part, which is the half that is easy to get wrong twice.
      upload: (file) => api<Attachment>(`/api/v1/chores/${taskId}/attachments`, {
        method: "POST",
        body: attachmentUploadBody(file)
      }),
      onSettled: ({ failedNames, failureReason, cancelled, uploadedCount }) => {
        if (cancelled) return;
        if (failedNames.length) {
          // Every fragment of this toast is resolved copy: the report itself
          // comes back from lib/backgroundUploads already in the reader's
          // language (#1088), and the subject and the follow-up sentence are
          // looked up here. `copy` rather than a `t`: this callback fires from a
          // detached queue, long after the screen that started it may have gone.
          const report = backgroundUploadFailureMessage(copy("uploads.subject.chore", "this chore"), failedNames, failureReason);
          showToast(`${report}. ${copy("uploads.openChoreToRetry", "Open the chore to try again.")}`, {
            link: toastLinkForEntity(copy("uploads.openChore", "Open chore"), "task", taskId)
          });
        }
        // This refresh cannot be left to the SSE echo. The upload's audit row is
        // self=true in the chores scope, and the create that kicked it off has
        // just marked that scope for a couple of seconds (#520) — an upload that
        // finishes inside that window would have its echo dropped and the
        // attachment would silently never appear. Refreshing here is the write's
        // own refresh, so it goes through refreshAfterOwnChange and the echo of
        // *this* upload is the one that gets deduped.
        if (uploadedCount > 0) void refreshAfterOwnChange("chores");
      }
    });
  }, [refreshAfterOwnChange, showToast]);

  const updateChore = useCallback(async (chore: ChoreOccurrence, form: HTMLFormElement) => {
    await run(async () => {
      await patchJson(`/api/v1/chores/${chore.taskId}`, chorePayload(form, chore));
      setChoreTimeline(await api<ChoreTimelineEntry[]>(`/api/v1/chores/${chore.taskId}/timeline`));
    }, { refresh: "chores", successMessage: "Chore updated" });
  }, [run, setChoreTimeline]);

  const deleteChore = useCallback(async (chore: ChoreOccurrence) => {
    await run(async () => {
      await deleteJson<{ ok?: true }>(`/api/v1/chores/${chore.taskId}`);
      setSelectedChore(null);
      setChoreTimeline([]);
    }, { refresh: "chores", successMessage: "Chore removed" });
  }, [run, setChoreTimeline, setSelectedChore]);

  const completeChoreOccurrence = useCallback(async (occurrence: ChoreOccurrence) => {
    let completed = false;
    let generatedOccurrenceIds: EntityReferenceId[] = [];
    await run(async () => {
      const result = await postJson<{ generatedOccurrenceIds?: EntityReferenceId[] }>(`/api/v1/chores/occurrences/${occurrence.id}/complete`, {
        completedAt: new Date().toISOString(),
        notes: "Quick complete"
      });
      generatedOccurrenceIds = result.generatedOccurrenceIds ?? [];
      completed = true;
    }, { refresh: "chores" });
    if (completed) {
      showToast(`${occurrence.title} done`, {
        actions: occurrenceUndoToastActions([occurrence], "Undo chore completion", "chores", generatedOccurrenceIds),
        link: choresListLink
      });
    }
  }, [run, showToast, occurrenceUndoToastActions]);

  const snoozeChoreOccurrence = useCallback(async (occurrence: ChoreOccurrence) => {
    let snoozed = false;
    await run(async () => {
      await postJson(`/api/v1/chores/occurrences/${occurrence.id}/snooze`, {
        snoozedUntil: nextSnoozeDate(occurrence.effectiveDueDate),
        reason: "Snoozed from chores"
      });
      snoozed = true;
    }, { refresh: "chores" });
    if (snoozed) {
      showToast(`${occurrence.title} snoozed`, {
        actions: occurrenceUndoToastActions([occurrence], "Undo chore snooze", "chores"),
        link: choresListLink
      });
    }
  }, [run, showToast, occurrenceUndoToastActions]);

  const skipChoreOccurrence = useCallback(async (occurrence: ChoreOccurrence) => {
    let skipped = false;
    let generatedOccurrenceIds: EntityReferenceId[] = [];
    await run(async () => {
      const result = await postJson<{ generatedOccurrenceIds?: EntityReferenceId[] }>(`/api/v1/chores/occurrences/${occurrence.id}/skip`, {
        skippedAt: new Date().toISOString(),
        reason: "Skipped from chores"
      });
      generatedOccurrenceIds = result.generatedOccurrenceIds ?? [];
      skipped = true;
    }, { refresh: "chores" });
    if (skipped) {
      showToast(`${occurrence.title} skipped`, {
        actions: occurrenceUndoToastActions([occurrence], "Undo chore skip", "chores", generatedOccurrenceIds),
        link: choresListLink
      });
    }
  }, [run, showToast, occurrenceUndoToastActions]);

  const reopenChoreOccurrence = useCallback(async (occurrence: ChoreOccurrence) => {
    await run(async () => {
      await postJson(`/api/v1/chores/occurrences/${occurrence.id}/reopen`, {
        reason: "Reopened from chores"
      });
    }, { refresh: "chores" });
  }, [run]);

  const applyChoreTemplate = useCallback(async (template: ChoreTemplate) => {
    let createdTaskId: EntityReferenceId | null = null;
    await run(async () => {
      const chore = await postJson<{ id: EntityReferenceId }>(`/api/v1/chores/templates/${template.id}/apply`, {
        dueDate: localDateOnly(),
        timezone: browserTimezone()
      });
      createdTaskId = chore.id;
    }, { refresh: "chores" });
    // Without visible feedback a successful apply looks like nothing happened
    // and gets re-tapped, duplicating the chore (#752).
    if (createdTaskId != null) {
      showToast(`${template.title} added`, {
        link: toastLinkForEntity("Open chore", "task", createdTaskId),
        actions: [
          {
            label: "Undo",
            onClick: () => {
              void run(async () => {
                await deleteJson<{ ok?: true }>(`/api/v1/chores/${createdTaskId}`);
              }, { refresh: "chores", successMessage: "Chore removed" });
            }
          }
        ]
      });
    }
  }, [run, showToast]);

  const openChoreTimeline = useCallback(async (chore: ChoreOccurrence) => {
    setSelectedChore(chore);
    setError("");
    try {
      setChoreTimeline(await api<ChoreTimelineEntry[]>(`/api/v1/chores/${chore.taskId}/timeline`));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load chore timeline");
    }
  }, [setChoreTimeline, setError, setSelectedChore]);

  return {
    createChore,
    uploadChoreAttachments,
    updateChore,
    deleteChore,
    completeChoreOccurrence,
    snoozeChoreOccurrence,
    skipChoreOccurrence,
    reopenChoreOccurrence,
    applyChoreTemplate,
    openChoreTimeline
  };
}
